import React, { useState, useEffect } from 'react';
import { User } from '../types';
import { userAPI } from '../utils/api';

interface SettingsScreenProps {
    currentUser: User;
    onBack: () => void;
    onLogout: () => void;
}

const SettingsScreen: React.FC<SettingsScreenProps> = ({
    currentUser,
    onBack,
    onLogout,
}) => {
    const [isMobile, setIsMobile] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteError, setDeleteError] = useState('');
    const [deleteSuccess, setDeleteSuccess] = useState('');
    const [isDeletingAccount, setIsDeletingAccount] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const handleAccountDeletion = async () => {
        if (!deletePassword.trim()) {
            setDeleteError('現在のパスワードを入力してください');
            setDeleteSuccess('');
            return;
        }

        setIsDeletingAccount(true);
        setDeleteError('');
        setDeleteSuccess('');

        try {
            const response = await userAPI.deleteAccount(
                currentUser.userId,
                deletePassword.trim()
            );

            const message =
                response.message || 'アカウントを削除しました。ご利用ありがとうございました。';
            setDeleteSuccess(message);

            setTimeout(() => {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                window.location.href = '/';
            }, 1500);
        } catch (error: any) {
            const message =
                error.response?.data?.error ||
                'アカウントの削除に失敗しました。パスワードをご確認ください。';
            setDeleteError(message);
        } finally {
            setIsDeletingAccount(false);
        }
    };

    return (
        <div
            style={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 50%, #dcdcdc 100%)',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: 'Arial, sans-serif',
                color: '#333',
            }}
        >
            {/* ヘッダー */}
            <div
                style={{
                    width: '100%',
                    padding: isMobile ? '15px 20px' : '20px 40px',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                    background: 'rgba(255, 255, 255, 0.9)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                }}
            >
                <button
                    onClick={onBack}
                    style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: isMobile ? '20px' : '24px',
                        padding: '5px 10px',
                        color: '#333',
                    }}
                >
                    ←
                </button>
                <h1
                    style={{
                        margin: 0,
                        fontSize: isMobile ? '18px' : '24px',
                        fontWeight: 'bold',
                        color: '#333',
                    }}
                >
                    設定
                </h1>
            </div>

            {/* メインコンテンツ */}
            <div
                style={{
                    flex: 1,
                    padding: isMobile ? '20px' : '40px',
                    overflow: 'auto',
                }}
            >
                <div
                    style={{
                        maxWidth: '800px',
                        margin: '0 auto',
                        background: 'rgba(255, 255, 255, 0.9)',
                        borderRadius: '15px',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        padding: isMobile ? '20px' : '30px',
                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                    }}
                >
                    {/* ユーザー情報セクション */}
                    <div
                        style={{
                            marginBottom: '30px',
                            paddingBottom: '30px',
                            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                        }}
                    >
                        <h2
                            style={{
                                fontSize: isMobile ? '16px' : '20px',
                                fontWeight: 'bold',
                                marginBottom: '15px',
                                color: '#333',
                            }}
                        >
                            ユーザー情報
                        </h2>
                        <div
                            style={{
                                fontSize: isMobile ? '13px' : '16px',
                                color: '#666',
                            }}
                        >
                            <div style={{ marginBottom: '10px' }}>
                                <strong>ユーザー名:</strong> {currentUser.username}
                            </div>
                            <div>
                                <strong>ユーザーID:</strong> {currentUser.userId}
                            </div>
                        </div>
                    </div>

                    {/* ログアウトセクション */}
                    <div>
                        <h2
                            style={{
                                fontSize: isMobile ? '16px' : '20px',
                                fontWeight: 'bold',
                                marginBottom: '15px',
                                color: '#333',
                            }}
                        >
                            アカウント
                        </h2>
                        <button
                            onClick={onLogout}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#ff4757',
                                cursor: 'pointer',
                                fontSize: isMobile ? '16px' : '18px',
                                fontWeight: 'bold',
                                textDecoration: 'underline',
                                padding: '10px 0',
                                display: 'block',
                            }}
                        >
                            ログアウト
                        </button>
                        <button
                            onClick={() => {
                                setShowDeleteConfirm((prev) => !prev);
                                setDeletePassword('');
                                setDeleteError('');
                                setDeleteSuccess('');
                            }}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#c62828',
                                cursor: 'pointer',
                                fontSize: isMobile ? '16px' : '18px',
                                fontWeight: 'bold',
                                textDecoration: 'underline',
                                padding: '6px 0 0',
                                display: 'block',
                            }}
                        >
                            アカウント削除
                        </button>
                        {showDeleteConfirm && (
                            <div
                                style={{
                                    marginTop: '15px',
                                    padding: '15px',
                                    borderRadius: '10px',
                                    border: '1px solid rgba(198, 40, 40, 0.3)',
                                    background: 'rgba(229, 57, 53, 0.08)',
                                }}
                            >
                                <p
                                    style={{
                                        fontSize: '12px',
                                        color: '#c62828',
                                        marginBottom: '10px',
                                        lineHeight: 1.6,
                                    }}
                                >
                                    パスワードを入力してアカウント削除を確定してください。削除後は元に戻せません。
                                </p>
                                {deleteError && (
                                    <div
                                        style={{
                                            background: 'rgba(255, 235, 59, 0.2)',
                                            color: '#c62828',
                                            padding: '8px',
                                            borderRadius: '6px',
                                            marginBottom: '10px',
                                            fontSize: '12px',
                                        }}
                                    >
                                        {deleteError}
                                    </div>
                                )}
                                {deleteSuccess && (
                                    <div
                                        style={{
                                            background: 'rgba(76, 175, 80, 0.15)',
                                            color: '#2e7d32',
                                            padding: '8px',
                                            borderRadius: '6px',
                                            marginBottom: '10px',
                                            fontSize: '12px',
                                        }}
                                    >
                                        {deleteSuccess}
                                    </div>
                                )}
                                <input
                                    type='password'
                                    value={deletePassword}
                                    onChange={(e) => setDeletePassword(e.target.value)}
                                    placeholder='現在のパスワード'
                                    autoComplete='current-password'
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '6px',
                                        border: '1px solid rgba(0, 0, 0, 0.2)',
                                        fontSize: '13px',
                                        marginBottom: '10px',
                                        boxSizing: 'border-box',
                                    }}
                                />
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: '10px',
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <button
                                        type='button'
                                        onClick={handleAccountDeletion}
                                        disabled={isDeletingAccount}
                                        style={{
                                            flex: 1,
                                            minWidth: '140px',
                                            padding: '10px',
                                            background: isDeletingAccount
                                                ? '#ccc'
                                                : 'linear-gradient(135deg, #e53935 0%, #d32f2f 100%)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: isDeletingAccount ? 'not-allowed' : 'pointer',
                                            fontSize: '13px',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {isDeletingAccount ? '削除中...' : '完全に削除する'}
                                    </button>
                                    <button
                                        type='button'
                                        onClick={() => {
                                            setShowDeleteConfirm(false);
                                            setDeletePassword('');
                                            setDeleteError('');
                                            setDeleteSuccess('');
                                        }}
                                        style={{
                                            flex: 1,
                                            minWidth: '100px',
                                            padding: '10px',
                                            background: 'rgba(0, 0, 0, 0.05)',
                                            color: '#333',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        キャンセル
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsScreen;

